import { useMemo } from 'react';

import { formatDate } from 'utils/dayjs';

import { CreatePayoutButton } from '@pages/Payouts/CreatePayoutButton';
import type { GetDisbursementStatsPayoutRequestsStateCategoriesParams } from 'api';
import { useGetDisbursementStatsPayoutRequestsStateCategories } from 'api';
import { Restricted } from 'containers/Restricted';

import { useOrganisationId } from 'hooks/context';
import { useGlobalFilters } from 'hooks/useGlobalFilters';
import { useOrgNavigate } from 'hooks/useOrgNavigate';

import { adaptPayoutResponse } from '../adapters/payoutStats.adapter';
import {
  PAYOUT_DOT_COLOR,
  STATUS_WIDGET_ERROR_MESSAGE,
  STATUS_WIDGET_PAYOUT_ARIA_LABEL,
  STATUS_WIDGET_PAYOUT_TEST_ID_PREFIX,
  STATUS_WIDGET_PAYOUT_TITLE
} from '../constants';
import { StatusWidget } from '../StatusWidget';

export const WidgetPayoutStatusesContainer = () => {
  const orgNavigate = useOrgNavigate();
  const organisationId = useOrganisationId<number>();
  const { filters } = useGlobalFilters();

  // we currently only use last 30 days, so using filters is misleading
  const dateRange =
    useMemo<GetDisbursementStatsPayoutRequestsStateCategoriesParams>(() => {
      const queryParams: GetDisbursementStatsPayoutRequestsStateCategoriesParams =
        {};
      if (filters?.dateRange?.from) {
        queryParams.created_after = formatDate(
          filters.dateRange.from,
          'utc',
          'iso'
        );
      }
      if (filters?.dateRange?.to) {
        queryParams.created_before = formatDate(
          filters.dateRange.to,
          'utc',
          'iso'
        );
      }
      return queryParams;
    }, [filters.dateRange]);

  const { data, isLoading, error } =
    useGetDisbursementStatsPayoutRequestsStateCategories(
      organisationId,
      dateRange,
      { query: { select: adaptPayoutResponse } }
    );

  const onBadgeClick = (status?: string) => {
    if (status) {
      orgNavigate(`/payouts?state=${status}`);
    } else {
      orgNavigate('/payouts');
    }
  };

  return (
    <StatusWidget
      ariaLabel={STATUS_WIDGET_PAYOUT_ARIA_LABEL}
      titleText={STATUS_WIDGET_PAYOUT_TITLE}
      headerLinkTo="/payouts"
      totalCount={data?.totalCount ?? 0}
      statusItems={data?.statusItems ?? []}
      onBadgeClick={onBadgeClick}
      testIdPrefix={STATUS_WIDGET_PAYOUT_TEST_ID_PREFIX}
      dotColorMap={PAYOUT_DOT_COLOR}
      loading={isLoading}
      error={error}
      errorMessage={STATUS_WIDGET_ERROR_MESSAGE}
      footer={
        <Restricted requiredPermission="exports:manage">
          <div className="pt-4">
            <CreatePayoutButton onFinish={() => orgNavigate('/payouts')} />
          </div>
        </Restricted>
      }
    />
  );
};
